import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ArrowBackOutlined from "@mui/icons-material/ArrowBackOutlined";
import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import PersonOutlined from "@mui/icons-material/PersonOutlined";

const MAX_IMAGE_BYTES = 50 * 1024 * 1024;
const MAX_DESCRIPTION = 200;
const ACCEPT_IMAGE = "image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png";

function recreateButtonSx(theme) {
  return {
    borderRadius: 2,
    fontWeight: 700,
    bgcolor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.45)}`,
    "&:hover": {
      bgcolor: theme.palette.primary.dark,
      color: theme.palette.primary.contrastText,
      boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.55)}`,
    },
    "&.Mui-disabled": {
      bgcolor: alpha(theme.palette.primary.main, 0.4),
      color: alpha(theme.palette.primary.contrastText, 0.85),
    },
  };
}

function ImageUploadField({
  label,
  hint,
  dropHint,
  icon: Icon,
  file,
  previewUrl,
  onPick,
  inputRef,
}) {
  const theme = useTheme();
  const [dragOver, setDragOver] = React.useState(false);

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {label}
      </Typography>
      <Box
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          onPick(event.dataTransfer?.files?.[0]);
        }}
        sx={{
          border: "1px dashed",
          borderColor: dragOver ? "primary.main" : alpha(theme.palette.text.primary, 0.22),
          borderRadius: 2,
          bgcolor: dragOver
            ? alpha(theme.palette.primary.main, 0.04)
            : alpha(theme.palette.action.hover, 0.02),
          minHeight: 148,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          overflow: "hidden",
          transition: "border-color 0.15s, background-color 0.15s",
        }}
      >
        {previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt=""
            sx={{ width: "100%", maxHeight: 200, objectFit: "contain", display: "block" }}
          />
        ) : (
          <Stack alignItems="center" spacing={1} sx={{ px: 2, py: 2.5, textAlign: "center" }}>
            <Icon sx={{ fontSize: 32, color: "text.disabled" }} />
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 260 }}>
              {dropHint}
            </Typography>
          </Stack>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
        {hint}
      </Typography>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={ACCEPT_IMAGE}
        onChange={(e) => onPick(e.target.files?.[0])}
      />
    </Box>
  );
}

export default function RecreateProductForm({
  lang,
  submitting = false,
  error: externalError = null,
  onBack,
  onSubmit,
}) {
  const theme = useTheme();
  const characterInputRef = React.useRef(null);
  const productInputRef = React.useRef(null);
  const [characterFile, setCharacterFile] = React.useState(null);
  const [characterPreview, setCharacterPreview] = React.useState(null);
  const [productFile, setProductFile] = React.useState(null);
  const [productPreview, setProductPreview] = React.useState(null);
  const [productDescription, setProductDescription] = React.useState("");
  const [dialog, setDialog] = React.useState("");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    return () => {
      if (characterPreview?.startsWith("blob:")) URL.revokeObjectURL(characterPreview);
      if (productPreview?.startsWith("blob:")) URL.revokeObjectURL(productPreview);
    };
  }, [characterPreview, productPreview]);

  const validateImage = (file) => {
    if (!file) return null;
    if (!file.type?.startsWith("image/")) {
      return lang?.exploreIdeasImageType || "Please upload a JPG or PNG image.";
    }
    if (file.size > MAX_IMAGE_BYTES) {
      return lang?.exploreIdeasImageTooLarge || "Image must be 50 MB or smaller.";
    }
    return null;
  };

  const pickCharacter = (file) => {
    const msg = validateImage(file);
    if (msg) {
      setError(msg);
      return;
    }
    if (!file) return;
    setError("");
    setCharacterFile(file);
    if (characterPreview?.startsWith("blob:")) URL.revokeObjectURL(characterPreview);
    setCharacterPreview(URL.createObjectURL(file));
  };

  const pickProduct = (file) => {
    const msg = validateImage(file);
    if (msg) {
      setError(msg);
      return;
    }
    if (!file) return;
    setError("");
    setProductFile(file);
    if (productPreview?.startsWith("blob:")) URL.revokeObjectURL(productPreview);
    setProductPreview(URL.createObjectURL(file));
  };

  const handleSubmit = () => {
    if (!characterFile) {
      setError(lang?.exploreIdeasCharacterRequired || "Character photo is required.");
      return;
    }
    if (!productFile) {
      setError(lang?.exploreIdeasImageRequired || "Product image is required.");
      return;
    }
    setError("");
    onSubmit?.({
      characterFile,
      productFile,
      productDescription: productDescription.trim(),
      dialog: dialog.trim(),
    });
  };

  return (
    <Stack spacing={2.5} sx={{ minWidth: 0 }}>
      <Button
        startIcon={<ArrowBackOutlined />}
        onClick={onBack}
        sx={{
          alignSelf: "flex-start",
          color: "text.secondary",
          fontWeight: 600,
          px: 0,
          "&:hover": { bgcolor: "transparent", color: "text.primary" },
        }}
      >
        {lang?.back || "Back"}
      </Button>

      <Box>
        <Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: 0.4 }}>
          {lang?.exploreIdeasRecreateHeading || "SWAP TALENT & ADD PRODUCT"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.65 }}>
          {lang?.exploreIdeasRecreateSub ||
            "Upload a character photo to replace the person in the reference clip, plus your product image and script details."}
        </Typography>
      </Box>

      <Alert severity="info" sx={{ py: 0.75 }}>
        {lang?.exploreIdeasVideoStudioHint ||
          "You'll continue in Video Studio → Video to Video with this template clip, your character, product, and prompt pre-filled."}
      </Alert>

      <ImageUploadField
        label={lang?.exploreIdeasCharacterImage || "Character / talent photo"}
        hint={lang?.exploreIdeasCharacterHint || "Portrait with head and shoulders visible. Max 50MB. JPG, PNG."}
        dropHint={
          lang?.exploreIdeasCharacterDrop ||
          "Upload the person who should replace the talent in the reference video"
        }
        icon={PersonOutlined}
        file={characterFile}
        previewUrl={characterPreview}
        onPick={pickCharacter}
        inputRef={characterInputRef}
      />

      <ImageUploadField
        label={lang?.exploreIdeasProductImage || "Product image"}
        hint={lang?.exploreIdeasProductImageHint || lang?.exploreIdeasImageHint || "Max 50MB. JPG, PNG."}
        dropHint={
          lang?.exploreIdeasImageDrop || "Drag and drop an image to upload or click to browse"
        }
        icon={ImageOutlined}
        file={productFile}
        previewUrl={productPreview}
        onPick={pickProduct}
        inputRef={productInputRef}
      />

      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {lang?.exploreIdeasProductDescription || "Product Description"}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder={
            lang?.exploreIdeasProductDescriptionPlaceholder || "Description of the product..."
          }
          value={productDescription}
          onChange={(e) => setProductDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {productDescription.length} / {MAX_DESCRIPTION}{" "}
          {lang?.characters || "characters"}
        </Typography>
      </Box>

      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {lang?.exploreIdeasDialog || "Dialog"}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder={lang?.exploreIdeasDialogPlaceholder || "What should the talent say..."}
          value={dialog}
          onChange={(e) => setDialog(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
        />
      </Box>

      {externalError || error ? (
        <Typography variant="body2" color="error.main" fontWeight={600}>
          {externalError || error}
        </Typography>
      ) : null}

      <Button
        variant="contained"
        color="primary"
        size="large"
        disabled={submitting}
        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighOutlined />}
        onClick={handleSubmit}
        sx={{ alignSelf: "flex-start", px: 3, ...recreateButtonSx(theme) }}
      >
        {lang?.exploreIdeasOpenVideoStudio || lang?.exploreIdeasRecreate || "Open in Video Studio"}
      </Button>
    </Stack>
  );
}
